import { DeleteOutlined } from '@ant-design/icons';
import { Popconfirm } from 'antd';
import React from 'react';
import { Props } from '.';
import CapSelector from '../../../../components/cap-selector/CapSelector';
import Icon from '../../../../components/icon/Icon';
import { CapNumber } from '../../../../core/models/CapNumber';
import { getFieldPlayIneligibleCapNumbers } from '../../../../helpers/EventHelper';
import {
    CreateMatchPlayerCommandOfMatchPlayer,
    EventVm,
    SwimoffRequest,
    UpdateEventCommand,
} from '../../../../utils/api';

class SwimOff extends React.PureComponent<Props> {
    public handleHomeTeamPlayerSelect = (cap: number): void => {
        const { event, updateEvent, lineups, homeTeamId } = this.props;

        const eventVm = event as EventVm;

        const lineup = lineups.find(
            (l: CreateMatchPlayerCommandOfMatchPlayer): boolean =>
                l.teamId === homeTeamId && l.number === cap
        );

        if (lineup) {
            const updatedEvent = new UpdateEventCommand({
                ...event,
                swimoff: event.swimoff
                    ? new SwimoffRequest({
                          ...event.swimoff,
                          homeTeamSwimmerId: lineup.id || 0,
                          id: eventVm.swimoffId || 0,
                      })
                    : new SwimoffRequest({
                          id: 0,
                          homeTeamSwimmerId: lineup.id || 0,
                          awayTeamSwimmerId: 0,
                          winnerSwimmerId: undefined,
                          winnerTeamId: undefined,
                      }),
            });

            updatedEvent.id = event.id;

            updateEvent(updatedEvent);
        }
    };

    public handleAwayTeamPlayerSelect = (cap: number): void => {
        const { event, updateEvent, lineups, homeTeamId } = this.props;

        const eventVm = event as EventVm;

        const lineup = lineups.find(
            (l: CreateMatchPlayerCommandOfMatchPlayer): boolean =>
                l.teamId !== homeTeamId && l.number === cap
        );

        if (lineup) {
            const updatedEvent = new UpdateEventCommand({
                ...event,
                swimoff: event.swimoff
                    ? new SwimoffRequest({
                          ...event.swimoff,
                          awayTeamSwimmerId: lineup.id || 0,
                          id: eventVm.swimoffId || 0,
                      })
                    : new SwimoffRequest({
                          id: 0,
                          homeTeamSwimmerId: 0,
                          awayTeamSwimmerId: lineup.id || 0,
                          winnerSwimmerId: undefined,
                          winnerTeamId: undefined,
                      }),
            });

            updatedEvent.id = event.id;

            updateEvent(updatedEvent);
        }
    };

    public handleHomeTeamPlayerClear = (): void => {
        const { event, updateEvent } = this.props;

        const eventVm = event as EventVm;
        const swim = event.swimoff;
        if (!swim) return;

        const homeId = swim.homeTeamSwimmerId;
        const winnerId = swim.winnerSwimmerId;
        const winnerWasHome = winnerId != null && homeId != null && winnerId === homeId;

        const updatedEvent = new UpdateEventCommand({
            ...event,
            swimoff: new SwimoffRequest({
                ...swim,
                homeTeamSwimmerId: 0,
                winnerSwimmerId: winnerWasHome ? undefined : winnerId,
                winnerTeamId: winnerWasHome ? undefined : swim.winnerTeamId,
                id: eventVm.swimoffId || 0,
            }),
        });

        updatedEvent.id = event.id;

        updateEvent(updatedEvent);
    };

    public handleAwayTeamPlayerClear = (): void => {
        const { event, updateEvent } = this.props;

        const eventVm = event as EventVm;
        const swim = event.swimoff;
        if (!swim) return;

        const awayId = swim.awayTeamSwimmerId;
        const winnerId = swim.winnerSwimmerId;
        const winnerWasAway = winnerId != null && awayId != null && winnerId === awayId;

        const updatedEvent = new UpdateEventCommand({
            ...event,
            swimoff: new SwimoffRequest({
                ...swim,
                awayTeamSwimmerId: 0,
                winnerSwimmerId: winnerWasAway ? undefined : winnerId,
                winnerTeamId: winnerWasAway ? undefined : swim.winnerTeamId,
                id: eventVm.swimoffId || 0,
            }),
        });

        updatedEvent.id = event.id;

        updateEvent(updatedEvent);
    };

    public handleWinnerSelect = (playerId: number): void => {
        const { event, updateEvent, lineups } = this.props;

        const eventVm = event as EventVm;

        const lineup = lineups.find(
            (l: CreateMatchPlayerCommandOfMatchPlayer): boolean => l.id === playerId
        );

        const updatedEvent = new UpdateEventCommand({
            ...event,
            swimoff: new SwimoffRequest({
                ...event.swimoff,
                winnerSwimmerId: playerId,
                winnerTeamId: lineup?.teamId,
                id: eventVm.swimoffId || 0,
            }),
        });

        updatedEvent.id = event.id;

        updateEvent(updatedEvent);
    };

    public handleWinnerClear = (): void => {
        const { event, updateEvent } = this.props;

        const eventVm = event as EventVm;

        const updatedEvent = new UpdateEventCommand({
            ...event,
            swimoff: new SwimoffRequest({
                ...event.swimoff,
                winnerSwimmerId: undefined,
                winnerTeamId: undefined,
                id: eventVm.swimoffId || 0,
            }),
        });

        updatedEvent.id = event.id;

        updateEvent(updatedEvent);
    };

    public render = (): React.ReactElement => {
        const { event, lineups, homeTeamId, clearEvent, deleteEvent, events } = this.props;
        const eventVm = event as EventVm;
        const eventExists = !!eventVm.id;

        const homeTeamLineups = lineups.filter(
            (l: CreateMatchPlayerCommandOfMatchPlayer): boolean => l.teamId === homeTeamId
        );
        const awayTeamLineups = lineups.filter(
            (l: CreateMatchPlayerCommandOfMatchPlayer): boolean => l.teamId !== homeTeamId
        );

        const homeSwimCapsDisabled = getFieldPlayIneligibleCapNumbers(events, homeTeamLineups);
        const awaySwimCapsDisabled = getFieldPlayIneligibleCapNumbers(events, awayTeamLineups);

        const homeTeamPlayerLineup = lineups.find(
            (l: CreateMatchPlayerCommandOfMatchPlayer): boolean =>
                l.id === event.swimoff?.homeTeamSwimmerId
        );
        const awayTeamPlayerLineup = lineups.find(
            (l: CreateMatchPlayerCommandOfMatchPlayer): boolean =>
                l.id === event.swimoff?.awayTeamSwimmerId
        );

        const swimOffWinnerId = event.swimoff?.winnerSwimmerId;

        // winnerSwimmerId is match player id (lineup.id), not roster playerId
        const isHomeWinner =
            homeTeamPlayerLineup != null &&
            swimOffWinnerId != null &&
            swimOffWinnerId === homeTeamPlayerLineup.id;
        const isAwayWinner =
            awayTeamPlayerLineup != null &&
            swimOffWinnerId != null &&
            swimOffWinnerId === awayTeamPlayerLineup.id;

        return (
            <div className="event-create match-form-lineup">
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                    }}
                >
                    <div className="title" style={{ fontSize: 16, marginBottom: 10 }}>
                        Pick the players who are swimming for the ball
                    </div>
                    <div className="c-shot-form__actions">
                        <span
                            className="c-shot-form__action-btn"
                            onClick={(): void => clearEvent()}
                            role="button"
                            title="Close"
                        >
                            <Icon name="icon-times" />
                        </span>
                        {eventExists && (
                            <Popconfirm
                                title="Are you sure you want to delete this event?"
                                onConfirm={(): void => deleteEvent(eventVm)}
                                okText="Delete"
                                okButtonProps={{ danger: true }}
                                cancelText="Cancel"
                            >
                                <span
                                    className="c-shot-form__action-btn c-shot-form__action-btn--delete"
                                    role="button"
                                    title="Delete"
                                >
                                    <DeleteOutlined />
                                </span>
                            </Popconfirm>
                        )}
                    </div>
                </div>
                <div className="title">Home team player:</div>
                <CapSelector
                    capClass="c-shot-form__cap-selector c-shot-form__cap-selector--home"
                    onSelect={this.handleHomeTeamPlayerSelect}
                    onClear={this.handleHomeTeamPlayerClear}
                    caps={homeTeamLineups.map(
                        (l: CreateMatchPlayerCommandOfMatchPlayer): CapNumber => ({
                            cap: l.number,
                            capNumber: l.capNumber || l.number,
                        })
                    )}
                    activeCap={homeTeamPlayerLineup ? homeTeamPlayerLineup.number : null}
                    activeCapNumber={
                        homeTeamPlayerLineup
                            ? homeTeamPlayerLineup.capNumber || homeTeamPlayerLineup.number
                            : null
                    }
                    disabledCaps={homeSwimCapsDisabled}
                />
                <br />
                <br />
                <div className="title">Away team player:</div>
                <CapSelector
                    capClass="c-shot-form__cap-selector c-shot-form__cap-selector--away"
                    onSelect={this.handleAwayTeamPlayerSelect}
                    onClear={this.handleAwayTeamPlayerClear}
                    caps={awayTeamLineups.map(
                        (l: CreateMatchPlayerCommandOfMatchPlayer): CapNumber => ({
                            cap: l.number,
                            capNumber: l.capNumber || l.number,
                        })
                    )}
                    activeCap={awayTeamPlayerLineup ? awayTeamPlayerLineup.number : null}
                    activeCapNumber={
                        awayTeamPlayerLineup
                            ? awayTeamPlayerLineup.capNumber || awayTeamPlayerLineup.number
                            : null
                    }
                    disabledCaps={awaySwimCapsDisabled}
                />
                <br />
                <br />
                {homeTeamPlayerLineup && awayTeamPlayerLineup && (
                    <div className="swimoff-winner-buttons">
                        <div className="c-shot-form__caps-label">Winner</div>
                        <div className="c-shot-form__grid">
                            <span
                                className={`c-shot-pill${
                                    isHomeWinner ? ' c-shot-pill--swimoff-winner-home' : ''
                                }`}
                                onClick={(): void =>
                                    this.handleWinnerSelect(homeTeamPlayerLineup.id || 0)
                                }
                                role="button"
                            >
                                {homeTeamPlayerLineup.capNumber || homeTeamPlayerLineup.number}
                            </span>
                            <span
                                className={`c-shot-pill${
                                    swimOffWinnerId == null ? ' c-shot-pill--active' : ''
                                }`}
                                onClick={(): void => this.handleWinnerClear()}
                                role="button"
                            >
                                No Winner
                            </span>
                            <span
                                className={`c-shot-pill${
                                    isAwayWinner ? ' c-shot-pill--swimoff-winner-away' : ''
                                }`}
                                onClick={(): void =>
                                    this.handleWinnerSelect(awayTeamPlayerLineup.id || 0)
                                }
                                role="button"
                            >
                                {awayTeamPlayerLineup.capNumber || awayTeamPlayerLineup.number}
                            </span>
                        </div>
                    </div>
                )}
            </div>
        );
    };
}

export default SwimOff;
